import { Button, TableItem } from 'components/atoms';
import Popup from 'components/molecules/popup';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useParamId } from 'hooks/useParamId';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiDownload } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetLabel } from 'services/labels';
import { usePostResidanceCertificate } from 'services/labels/residanceCertificate';
import {
  ILabelForm,
  ILabelResidenceCertificateMutation,
  ILabelResidenceCertificateResponse,
} from 'services/labels/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { getNodes } from 'utils/table/nodes.utils';

const LabelResidenceCertificate = () => {
  const { t } = useTranslation();
  const labelId = useParamId('label');

  const { status, values }: IFormikContextType<ILabelForm> =
    useFormikContext<ILabelForm>();

  const { refetch } = useGetLabel(labelId);

  const addResidanceCertificate = usePostResidanceCertificate(labelId);

  const tableData = getNodes<ILabelResidenceCertificateResponse>(
    values.residenceCertificates
  );

  const onAddResidanceCertificate = async (
    certificate: ILabelResidenceCertificateMutation,
    actions: PopupActions
  ) => {
    await addResidanceCertificate.mutateAsync(certificate, {
      onSuccess: async () => {
        await refetch();
        actions.close();
      },
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={<></>}
          topbarContent={
            <Popup.LabelResidanceCertificate
              trigger={
                <Button variant={BUTTON_VARIANT.submit}>
                  <Button.Text text={t('button.add')} />
                </Button>
              }
              onAdd={onAddResidanceCertificate}
            />
          }
        />
      }>
      <Table
        id={'LabelResidenceCertificate2'}
        size='compact'
        data={tableData}
        columns={[
          {
            label: t('input.label.country'),
            renderCell: ({ country }) => (
              <TableItem justifyPosition='start' title={country.codeAlfa2}>
                <TableItem.Text
                  text={
                    i18next.language === 'pl'
                      ? country.polishName
                      : country.englishName
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.expirationDate'),
            renderCell: ({ expirationDate }) => (
              <TableItem
                justifyPosition='start'
                title={formatDate(expirationDate)}>
                <TableItem.Text text={formatDate(expirationDate)} />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.scan'),
            renderCell: ({ residenceScan }) => (
              <TableItem justifyPosition='start'>
                {residenceScan ? (
                  <>
                    <TableItem.Text text={residenceScan.fileName} />
                    <TableItem.Text
                      text={`${formatDate(
                        residenceScan.createdAt
                      )}, ${residenceScan.sizeInMB.toFixed(2)} MB`}
                      isSupplementary
                    />
                  </>
                ) : (
                  <TableItem.Text
                    text={t('label.certificates.no-certificate-scan')}
                    isSupplementary
                  />
                )}
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ residenceScan }) => (
              <TableItem justifyPosition='end'>
                <a
                  href={residenceScan?.path}
                  download={residenceScan?.path}
                  aria-disabled={!residenceScan?.path}
                  target='_blank'
                  rel='noreferrer'>
                  <Button
                    style={{ opacity: residenceScan ? '1' : '0' }}
                    isClear
                    onlyIcon
                    disabled={!residenceScan}>
                                        <Button.Description description={t('button.download')} />

                                        <Button.Icon icon={<PiDownload />} />
                  </Button>
                </a>
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default LabelResidenceCertificate;
